import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Building2, Plus, Sparkles, TrendingUp, Check, Newspaper, Filter, Bookmark, BookmarkPlus, X, Mail, Loader2, Copy, Globe, ShieldCheck, ChevronDown, Cpu, ExternalLink, AlertTriangle, Settings2, History, Upload, Trash2, Clock, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Loading, EmptyState, AITag } from "../components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const FIT_STYLES = {
  Strong: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Good: "bg-sky-100 text-sky-700 border-sky-200",
  Possible: "bg-slate-100 text-slate-600 border-slate-200",
};
const CONFIDENCE = {
  high: { label: "High confidence", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  medium: { label: "Medium confidence", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  low: { label: "Low confidence", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};
const REGIONS = ["Singapore", "Malaysia", "Indonesia", "Thailand", "Vietnam", "Philippines", "Southeast Asia"];
const SECTORS = ["Any", "Energy", "Manufacturing", "Real Estate", "Transport & Logistics", "Agriculture", "Food & Beverage", "Financial Services", "Technology", "Retail & Apparel", "Hospitality"];
const INTERVALS = [{ v: 6, l: "Every 6 hours" }, { v: 12, l: "Every 12 hours" }, { v: 24, l: "Daily" }, { v: 48, l: "Every 2 days" }, { v: 168, l: "Weekly" }];

const ago = (d) => (d === 0 ? "today" : d === 1 ? "1 day ago" : `${d} days ago`);
const when = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

function FactorBreakdown({ factors }) {
  return (
    <div className="mt-3 space-y-2.5 bg-slate-50 rounded-xl p-3.5 border border-slate-100">
      {factors.map((f) => (
        <div key={f.key}>
          <div className="flex items-center gap-2">
            <span className="w-32 flex-shrink-0 text-xs font-medium text-slate-700">{f.label}</span>
            <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${f.score}%` }} />
            </div>
            <span className="w-8 text-right text-xs tabular-nums font-semibold text-slate-700">{f.score}</span>
            <span className="w-9 text-right text-[10px] tabular-nums text-slate-400">{f.weight}%</span>
          </div>
          {f.reason && <div className="text-[11px] text-slate-500 mt-0.5 ml-[136px] leading-snug">{f.reason}</div>}
        </div>
      ))}
    </div>
  );
}

export default function Finder() {
  const qc = useQueryClient();
  const [added, setAdded] = useState({});
  const [industry, setIndustry] = useState("all");
  const [country, setCountry] = useState("all");
  const [saveOpen, setSaveOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [outreachFor, setOutreachFor] = useState(null);
  const [outreachText, setOutreachText] = useState("");
  const [region, setRegion] = useState("Singapore");
  const [sector, setSector] = useState("Any");
  const [expanded, setExpanded] = useState({});
  const [activeRunId, setActiveRunId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [viewRun, setViewRun] = useState(null);

  const { data: meta } = useQuery({ queryKey: ["finder-meta"], queryFn: async () => (await api.get("/finder/meta")).data });
  const { data: signals } = useQuery({ queryKey: ["finder-signals"], queryFn: async () => (await api.get("/finder/signals")).data });
  const { data: savedSearches } = useQuery({ queryKey: ["saved-searches"], queryFn: async () => (await api.get("/finder/saved-searches")).data });
  const { data: status } = useQuery({ queryKey: ["discovery-status"], queryFn: async () => (await api.get("/finder/discovery-status")).data });
  const { data: prospects, isLoading } = useQuery({
    queryKey: ["prospects", industry, country, viewRun?.id],
    queryFn: async () => (await api.get("/finder/prospects", { params: { industry, country, run_id: viewRun?.id } })).data,
  });

  // poll the active discovery run until it completes
  const { data: activeRun } = useQuery({
    queryKey: ["discovery-run", activeRunId],
    queryFn: async () => (await api.get(`/finder/runs/${activeRunId}`)).data,
    enabled: !!activeRunId,
    refetchInterval: (q) => { const s = q.state.data?.status; return s === "done" || s === "error" ? false : 3500; },
  });
  useEffect(() => {
    if (!activeRun) return;
    if (activeRun.status === "done") {
      toast.success(`Discovered ${activeRun.count} organisations in ${activeRun.region}`);
      qc.invalidateQueries({ queryKey: ["prospects"] });
      qc.invalidateQueries({ queryKey: ["finder-meta"] });
      qc.invalidateQueries({ queryKey: ["finder-signals"] });
      qc.invalidateQueries({ queryKey: ["discovery-status"] });
      qc.invalidateQueries({ queryKey: ["discovery-runs"] });
      setActiveRunId(null);
    } else if (activeRun.status === "error") {
      toast.error("Discovery run failed — please try again.");
      setActiveRunId(null);
    }
  }, [activeRun?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = useMutation({
    mutationFn: (id) => api.post(`/finder/prospects/${id}/add`),
    onSuccess: (r, id) => { setAdded((a) => ({ ...a, [id]: true })); qc.invalidateQueries({ queryKey: ["opps"] }); toast.success("Added to pipeline as a lead"); },
  });
  const discover = useMutation({
    mutationFn: () => api.post("/finder/discover", { region, sector, count: 8 }),
    onSuccess: (r) => { setActiveRunId(r.data.run_id); toast("Discovery started — surfacing and scoring prospects…"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not start discovery"),
  });
  const saveSearch = useMutation({
    mutationFn: () => api.post("/finder/saved-searches", { name: searchName, industry, country }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["saved-searches"] }); setSaveOpen(false); setSearchName(""); toast.success("Search saved"); },
  });
  const delSearch = useMutation({
    mutationFn: (id) => api.delete(`/finder/saved-searches/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-searches"] }),
  });

  const applySearch = (s) => { setIndustry(s.industry || "all"); setCountry(s.country || "all"); };
  const labelFor = (s) => [s.industry !== "all" ? s.industry : null, s.country !== "all" ? s.country : null].filter(Boolean).join(" · ") || "All prospects";

  const outreach = useMutation({
    mutationFn: (id) => api.post(`/finder/prospects/${id}/outreach`),
    onSuccess: (r) => setOutreachText(r.data.email),
  });
  const openOutreach = (p) => { setOutreachFor(p); setOutreachText(""); outreach.mutate(p.id); };

  const discovering = discover.isPending || !!activeRunId;

  if (isLoading || !meta) return <Loading />;

  const aiCount = prospects.filter((p) => p.source === "ai_discovered").length;

  return (
    <div>
      <PageHeader eyebrow="Opportunity Finder" title="Likely Prospects"
        description="AI discovers real Singapore & SEA organisations from the open web and scores each on its likelihood of needing ESG advisory — alongside your curated demo prospects. You decide who to pursue." />

      {/* AI Discovery control */}
      <div className="bg-slate-900 text-white rounded-2xl p-5 mb-6" data-testid="discover-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center"><Cpu className="w-5 h-5 text-emerald-400" /></div>
            <div>
              <div className="font-display font-semibold text-sm flex items-center gap-2">AI Opportunity Discovery <span className="text-[10px] font-mono text-emerald-400 uppercase tracking-wider">live web</span></div>
              <div className="text-xs text-slate-400 mt-0.5">Finds real organisations, verifies each against its own website, and scores ESG-advisory propensity.</div>
            </div>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Region</div>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger className="w-40 h-9 bg-slate-800 border-slate-700 text-white" data-testid="discover-region"><SelectValue /></SelectTrigger>
                <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Sector</div>
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger className="w-44 h-9 bg-slate-800 border-slate-700 text-white" data-testid="discover-sector"><SelectValue /></SelectTrigger>
                <SelectContent>{SECTORS.map((s) => <SelectItem key={s} value={s}>{s === "Any" ? "Any sector" : s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={() => discover.mutate()} disabled={discovering} data-testid="discover-run" className="h-9 bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-semibold">
              {discovering ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Discovering…</> : <><Globe className="w-4 h-4 mr-1.5" /> Discover</>}
            </Button>
            <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="discover-settings" className="h-9 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"><Settings2 className="w-4 h-4" /></Button>
            <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="discover-history" className="h-9 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"><History className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-700/60 text-[11px] text-slate-400 flex-wrap" data-testid="discovery-status">
          {discovering ? (
            <span className="flex items-center gap-1.5 text-emerald-400"><Loader2 className="w-3 h-3 animate-spin" /> Proposing candidates, verifying their websites, and scoring… this runs in the background (up to a minute).</span>
          ) : status?.last_run ? (
            <span>Last run: <b className="text-slate-200">{status.last_run.count}</b> prospects in {status.last_run.region}{status.last_run.sector && status.last_run.sector !== "Any" ? ` · ${status.last_run.sector}` : ""} — <b className="text-slate-200">{status.last_run.verified}</b> website-verified{status.last_run.sgx_verified ? `, ${status.last_run.sgx_verified} SGX-verified` : ""}</span>
          ) : (
            <span>No AI runs yet — pick a region and click Discover to surface fresh prospects.</span>
          )}
          {status?.scheduled?.enabled && <span className="flex items-center gap-1"><Radar className="w-3 h-3 text-emerald-400" /> Auto-refresh {INTERVALS.find((i) => i.v === status.scheduled.interval_hours)?.l.toLowerCase() || `every ${status.scheduled.interval_hours}h`}</span>}
          {aiCount > 0 && <span className="ml-auto text-emerald-400">{aiCount} AI-discovered in view</span>}
        </div>
      </div>

      {/* Run filter banner */}
      {viewRun && (
        <div className="flex items-center justify-between gap-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5" data-testid="run-filter-banner">
          <span className="text-sm text-emerald-800 flex items-center gap-2"><History className="w-4 h-4" /> Showing prospects from the {viewRun.region}{viewRun.sector && viewRun.sector !== "Any" ? ` · ${viewRun.sector}` : ""} run on {when(viewRun.finished_at || viewRun.started_at)}</span>
          <Button variant="ghost" size="sm" onClick={() => setViewRun(null)} data-testid="clear-run-filter" className="text-emerald-700 hover:text-emerald-900">Show all <X className="w-3.5 h-3.5 ml-1" /></Button>
        </div>
      )}

      {/* Signals feed */}
      {signals?.length > 0 && !viewRun && (
        <div className="bg-slate-900 text-white rounded-2xl p-5 mb-6 overflow-hidden" data-testid="signals-feed">
          <div className="flex items-center gap-2 mb-3"><Newspaper className="w-4 h-4 text-emerald-400" /><span className="font-display font-semibold text-sm">Fresh sustainability signals</span><span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">live feed</span></div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {signals.slice(0, 8).map((s, i) => (
              <div key={i} className="flex-shrink-0 w-64 bg-slate-800/60 border border-slate-700/50 rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-mono text-emerald-400 uppercase">{s.category}</span>
                  <span className="text-[10px] text-slate-400">{ago(s.days_ago)}</span>
                </div>
                <div className="text-xs text-slate-100 leading-snug">{s.headline}</div>
                <div className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">{s.prospect} · {s.source}{s.ai_discovered && <span className="text-emerald-400">· AI</span>}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="flex items-center gap-1.5 text-sm text-slate-500"><Filter className="w-4 h-4" /> Filter</span>
        <Select value={industry} onValueChange={setIndustry}>
          <SelectTrigger className="w-52 h-9 bg-white" data-testid="filter-industry"><SelectValue placeholder="Industry" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All industries</SelectItem>{meta.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className="w-44 h-9 bg-white" data-testid="filter-country"><SelectValue placeholder="Region" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All regions</SelectItem>{meta.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        {(industry !== "all" || country !== "all") && <Button variant="ghost" size="sm" onClick={() => { setIndustry("all"); setCountry("all"); }} data-testid="clear-filters">Clear</Button>}
        <Button variant="outline" size="sm" onClick={() => setSaveOpen(true)} data-testid="save-search" className="h-9"><BookmarkPlus className="w-4 h-4 mr-1.5" /> Save search</Button>
        <span className="text-sm text-slate-400 ml-auto">{prospects.length} match{prospects.length === 1 ? "" : "es"}</span>
      </div>

      {/* Saved searches */}
      {(savedSearches || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="saved-searches">
          <span className="text-xs text-slate-400 flex items-center gap-1"><Bookmark className="w-3.5 h-3.5" /> Saved:</span>
          {savedSearches.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full pl-3 pr-1.5 py-1">
              <button onClick={() => applySearch(s)} data-testid={`apply-search-${s.id}`} className="font-medium text-slate-700 hover:text-emerald-600">{s.name}</button>
              <span className="text-slate-400">· {labelFor(s)}</span>
              <button onClick={() => delSearch.mutate(s.id)} data-testid={`delete-search-${s.id}`} className="text-slate-300 hover:text-rose-500"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      {prospects.length === 0 ? (
        <EmptyState icon={Radar} title="No prospects match" description="Try widening your filters, or run an AI discovery for a region above." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {prospects.map((p, i) => {
            const isAI = p.source === "ai_discovered";
            const conf = CONFIDENCE[p.confidence] || CONFIDENCE.medium;
            return (
            <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.4) }}
              data-testid={`prospect-${p.id}`} className={`bg-white rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow ${isAI ? "border-emerald-200/80" : "border-slate-200/80"}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isAI ? "bg-emerald-600 text-white" : "bg-slate-900 text-white"}`}>{isAI ? <Cpu className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}</div>
                  <div>
                    <div className="font-display font-semibold text-slate-900 flex items-center gap-2">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.industry} · {p.country}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-bold text-emerald-600">{p.match_score}<span className="text-sm text-slate-400">%</span></div>
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border ${FIT_STYLES[p.fit]}`}>{p.fit} fit</span>
                </div>
              </div>

              {isAI && (
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  <span data-testid={`ai-badge-${p.id}`} className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-600 text-white"><Sparkles className="w-3 h-3" /> AI-discovered</span>
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${conf.cls}`}>
                    {p.confidence === "low" ? <AlertTriangle className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />} {conf.label}
                  </span>
                  {p.verified && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"><Globe className="w-3 h-3" /> website verified</span>}
                  {p.sgx_verified ? (
                    <span data-testid={`sgx-verified-${p.id}`} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white"><CheckCircle2 className="w-3 h-3" /> SGX-listed ✓</span>
                  ) : p.sgx_listed && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">SGX / regulated (est.)</span>
                  )}
                </div>
              )}

              {isAI && p.ai_summary && <p className="text-sm text-slate-600 mt-3 leading-relaxed">{p.ai_summary}</p>}

              {!isAI && (
                <div className="mt-4 space-y-1.5">
                  {p.reasons.map((r, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> {r}
                    </div>
                  ))}
                </div>
              )}

              {isAI && p.factors?.length > 0 && (
                <div className="mt-3">
                  <button onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))} data-testid={`toggle-factors-${p.id}`}
                    className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-emerald-600">
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded[p.id] ? "rotate-180" : ""}`} /> Why this score — factor breakdown
                  </button>
                  <AnimatePresence>
                    {expanded[p.id] && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <FactorBreakdown factors={p.factors} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {p.news?.length > 0 && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <div className="text-[10px] font-mono uppercase text-slate-400 mb-1.5 flex items-center gap-1"><Newspaper className="w-3 h-3" /> {isAI ? "Signals (indicative)" : "Latest signals"}</div>
                  {p.news.map((n, idx) => (
                    <div key={idx} className="flex items-start justify-between gap-2 py-0.5">
                      <span className="text-xs text-slate-600 leading-snug">{n.headline}</span>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap flex-shrink-0">{ago(n.days_ago)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-1.5 mt-4">
                {p.suggested_services.map((s) => <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{s}</span>)}
              </div>

              <div className="mt-5 flex justify-between items-center gap-2">
                {isAI && p.website ? (
                  <a href={p.website} target="_blank" rel="noreferrer" data-testid={`visit-${p.id}`} className="text-xs text-slate-400 hover:text-emerald-600 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Visit site</a>
                ) : <span />}
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openOutreach(p)} data-testid={`outreach-${p.id}`}>
                    <Mail className="w-4 h-4 mr-1.5" /> Draft outreach
                  </Button>
                  {added[p.id] ? (
                    <span className="text-sm text-emerald-600 flex items-center gap-1.5"><Check className="w-4 h-4" /> Added</span>
                  ) : (
                    <Button size="sm" onClick={() => add.mutate(p.id)} disabled={add.isPending} data-testid={`add-prospect-${p.id}`} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-1.5" /> Add to pipeline
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          );})}
        </div>
      )}
      <p className="mt-8 text-xs text-slate-400 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> AI-discovered prospects are best-effort from live web data and verified against their own websites; signals are indicative and SGX status is estimated unless matched to your uploaded list. Always qualify a prospect before reaching out.</p>

      <SaveSearchDialog {...{ saveOpen, setSaveOpen, industry, country, searchName, setSearchName, saveSearch }} />
      <OutreachDialog {...{ outreachFor, setOutreachFor, outreachText, setOutreachText, outreach }} />
      <SettingsDialog {...{ settingsOpen, setSettingsOpen, status }} />
      <HistoryDialog {...{ historyOpen, setHistoryOpen, setViewRun }} />
    </div>
  );
}

function SaveSearchDialog({ saveOpen, setSaveOpen, industry, country, searchName, setSearchName, saveSearch }) {
  return (
    <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>Save this search</DialogTitle><DialogDescription>Save the current filter combination for quick access later.</DialogDescription></DialogHeader>
        <div className="text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
          Industry: <b>{industry === "all" ? "All" : industry}</b> · Region: <b>{country === "all" ? "All" : country}</b>
        </div>
        <div><Label>Name</Label><Input value={searchName} onChange={(e) => setSearchName(e.target.value)} data-testid="search-name" className="mt-1.5" placeholder="e.g. Singapore energy targets" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveSearch.mutate()} disabled={!searchName || saveSearch.isPending} data-testid="save-search-confirm">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OutreachDialog({ outreachFor, setOutreachFor, outreachText, setOutreachText, outreach }) {
  return (
    <Dialog open={!!outreachFor} onOpenChange={(o) => { if (!o) { setOutreachFor(null); setOutreachText(""); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Outreach email — {outreachFor?.name}</DialogTitle>
          <DialogDescription>An AI-drafted suggestion grounded in the freshest signal — review and edit before you send.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between">
          <AITag />
          <Button variant="ghost" size="sm" onClick={() => outreach.mutate(outreachFor.id)} disabled={outreach.isPending} data-testid="outreach-regenerate">
            {outreach.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Regenerate
          </Button>
        </div>
        {outreach.isPending && !outreachText ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Drafting a Singapore-context email from the freshest signal…</div>
        ) : (
          <Textarea value={outreachText} onChange={(e) => setOutreachText(e.target.value)} data-testid="outreach-text" className="min-h-[280px] text-sm leading-relaxed font-mono" />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOutreachFor(null); setOutreachText(""); }}>Close</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="outreach-copy" disabled={!outreachText}
            onClick={() => { navigator.clipboard.writeText(outreachText); toast.success("Copied — review before sending"); }}>
            <Copy className="w-4 h-4 mr-1.5" /> Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({ settingsOpen, setSettingsOpen, status }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const { data: settings } = useQuery({ queryKey: ["finder-settings"], queryFn: async () => (await api.get("/finder/settings")).data, enabled: settingsOpen });
  const [weights, setWeights] = useState(null);
  const [sched, setSched] = useState(null);
  const [sgxText, setSgxText] = useState("");

  useEffect(() => { if (settings) setWeights({ ...settings.weights }); }, [settings]);
  useEffect(() => { if (status?.scheduled) setSched({ ...status.scheduled }); }, [status]);

  const saveWeights = useMutation({
    mutationFn: () => api.put("/finder/settings", { weights }),
    onSuccess: (r) => { setWeights({ ...r.data.weights }); qc.invalidateQueries({ queryKey: ["finder-settings"] }); toast.success("Scoring weights saved (normalised to 100)"); },
  });
  const saveSched = useMutation({
    mutationFn: () => api.put("/finder/discovery-config", { enabled: sched.enabled, interval_hours: sched.interval_hours, region: sched.region, sector: sched.sector, count: sched.count }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["discovery-status"] }); toast.success("Scheduled discovery updated"); },
  });
  const saveSgx = useMutation({
    mutationFn: () => api.post("/finder/sgx-list", { text: sgxText }),
    onSuccess: (r) => { setSgxText(""); qc.invalidateQueries({ queryKey: ["finder-settings"] }); toast.success(`Saved ${r.data.count} SGX-listed companies`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save list"),
  });
  const uploadSgx = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/finder/sgx-list/upload", fd); },
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["finder-settings"] }); toast.success(`Uploaded ${r.data.count} SGX-listed companies`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not read file"),
  });
  const clearSgx = useMutation({
    mutationFn: () => api.delete("/finder/sgx-list"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["finder-settings"] }); toast.success("SGX list cleared"); },
  });

  const total = weights ? Object.values(weights).reduce((a, b) => a + Number(b), 0) : 0;

  return (
    <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Discovery settings</DialogTitle>
          <DialogDescription>Tune how the AI finds and scores prospects, and fact-check SGX status against your own list.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="schedule">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
            <TabsTrigger value="weights" data-testid="tab-weights">Scoring</TabsTrigger>
            <TabsTrigger value="sgx" data-testid="tab-sgx">SGX list</TabsTrigger>
          </TabsList>

          <TabsContent value="schedule" className="space-y-4 pt-2">
            {sched && (<>
              <div className="flex items-center justify-between">
                <div><div className="text-sm font-medium text-slate-800">Automatic refresh</div><div className="text-xs text-slate-500">Run discovery on a schedule into a shared feed.</div></div>
                <Switch checked={!!sched.enabled} onCheckedChange={(v) => setSched((s) => ({ ...s, enabled: v }))} data-testid="sched-enabled" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Cadence</Label>
                  <Select value={String(sched.interval_hours)} onValueChange={(v) => setSched((s) => ({ ...s, interval_hours: Number(v) }))}>
                    <SelectTrigger className="mt-1 h-9" data-testid="sched-interval"><SelectValue /></SelectTrigger>
                    <SelectContent>{INTERVALS.map((i) => <SelectItem key={i.v} value={String(i.v)}>{i.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Prospects per run</Label>
                  <Select value={String(sched.count)} onValueChange={(v) => setSched((s) => ({ ...s, count: Number(v) }))}>
                    <SelectTrigger className="mt-1 h-9" data-testid="sched-count"><SelectValue /></SelectTrigger>
                    <SelectContent>{[4, 6, 8, 10, 12].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Region</Label>
                  <Select value={sched.region} onValueChange={(v) => setSched((s) => ({ ...s, region: v }))}>
                    <SelectTrigger className="mt-1 h-9" data-testid="sched-region"><SelectValue /></SelectTrigger>
                    <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Sector</Label>
                  <Select value={sched.sector} onValueChange={(v) => setSched((s) => ({ ...s, sector: v }))}>
                    <SelectTrigger className="mt-1 h-9" data-testid="sched-sector"><SelectValue /></SelectTrigger>
                    <SelectContent>{SECTORS.map((s) => <SelectItem key={s} value={s}>{s === "Any" ? "Any sector" : s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => saveSched.mutate()} disabled={saveSched.isPending} data-testid="save-schedule">Save schedule</Button>
            </>)}
          </TabsContent>

          <TabsContent value="weights" className="space-y-3 pt-2">
            <p className="text-xs text-slate-500">Adjust how much each factor drives the overall propensity score. Weights are normalised to 100 on save.</p>
            {weights && (settings?.factors || []).map((f) => (
              <div key={f.key}>
                <div className="flex items-center justify-between text-sm"><span className="text-slate-700">{f.label}</span><span className="tabular-nums font-semibold text-slate-800">{weights[f.key]}%</span></div>
                <Slider value={[weights[f.key]]} min={0} max={60} step={1} onValueChange={([v]) => setWeights((w) => ({ ...w, [f.key]: v }))} data-testid={`weight-${f.key}`} className="mt-1.5" />
              </div>
            ))}
            <div className="flex items-center justify-between pt-1">
              <span className={`text-xs font-medium ${total === 100 ? "text-emerald-600" : "text-amber-600"}`}>Total: {total}% {total !== 100 && "(will be normalised to 100)"}</span>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveWeights.mutate()} disabled={saveWeights.isPending} data-testid="save-weights">Save weights</Button>
            </div>
          </TabsContent>

          <TabsContent value="sgx" className="space-y-3 pt-2">
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2" data-testid="sgx-summary">
              {settings?.sgx?.count ? (<>
                <b>{settings.sgx.count}</b> SGX-listed companies on file{settings.sgx.updated_at ? ` · updated ${when(settings.sgx.updated_at)}` : ""}.
                <div className="text-xs text-slate-500 mt-1">{settings.sgx.sample.join(", ")}{settings.sgx.count > settings.sgx.sample.length ? "…" : ""}</div>
              </>) : "No SGX list yet — upload one so regulatory exposure is fact-checked, not just estimated."}
            </div>
            <div>
              <Label className="text-xs">Paste company names (one per line or comma-separated)</Label>
              <Textarea value={sgxText} onChange={(e) => setSgxText(e.target.value)} data-testid="sgx-text" className="mt-1 min-h-[110px] text-sm" placeholder={"Keppel Ltd\nCapitaLand Investment\nSembcorp Industries"} />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveSgx.mutate()} disabled={!sgxText.trim() || saveSgx.isPending} data-testid="save-sgx">Save list</Button>
              <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" data-testid="sgx-file" onChange={(e) => { if (e.target.files[0]) uploadSgx.mutate(e.target.files[0]); e.target.value = ""; }} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploadSgx.isPending} data-testid="upload-sgx"><Upload className="w-4 h-4 mr-1.5" /> Upload CSV/TXT</Button>
              {settings?.sgx?.count > 0 && <Button size="sm" variant="ghost" className="text-rose-500 hover:text-rose-600 ml-auto" onClick={() => clearSgx.mutate()} data-testid="clear-sgx"><Trash2 className="w-4 h-4 mr-1.5" /> Clear</Button>}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ historyOpen, setHistoryOpen, setViewRun }) {
  const { data: runs, isLoading } = useQuery({ queryKey: ["discovery-runs"], queryFn: async () => (await api.get("/finder/discovery-runs")).data, enabled: historyOpen });
  return (
    <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Discovery history</DialogTitle>
          <DialogDescription>Every discovery run — revisit an earlier batch of prospects.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[420px] overflow-y-auto -mx-1 px-1 space-y-2" data-testid="history-list">
          {isLoading ? <div className="text-sm text-slate-400 py-8 text-center">Loading…</div> :
            !runs?.length ? <div className="text-sm text-slate-400 py-8 text-center">No runs yet.</div> :
            runs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={`run-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                    {r.region}{r.sector && r.sector !== "Any" ? ` · ${r.sector}` : ""}
                    {r.scheduled && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">auto</span>}
                    {r.status === "running" && <span className="text-[10px] text-emerald-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> running</span>}
                    {r.status === "error" && <span className="text-[10px] text-rose-500">failed</span>}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {when(r.finished_at || r.started_at)} · {r.count} prospects{r.verified ? ` · ${r.verified} verified` : ""}{r.sgx_verified ? ` · ${r.sgx_verified} SGX✓` : ""}</div>
                </div>
                {r.status === "done" && r.count > 0 && (
                  <Button size="sm" variant="outline" onClick={() => { setViewRun(r); setHistoryOpen(false); }} data-testid={`view-run-${r.id}`}>View prospects</Button>
                )}
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
